"use client";

import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import type { TenantDetail } from "@/lib/db/queries/tenants";

const dateFmt = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const dateTimeFmt = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const priceFmt = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const SUB_STATUS_LABEL = {
  trialing: "En essai",
  active: "Actif (paiement OK)",
  past_due: "Paiement en échec",
  canceled: "Annulé",
} as const;

const SUB_STATUS_TONE = {
  trialing: "warning",
  active: "success",
  past_due: "danger",
  canceled: "neutral",
} as const;

function daysBetween(end: Date | null): number | null {
  if (!end) return null;
  const endDate = new Date(end);
  const now = new Date();
  const utcEnd = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const utcNow = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((utcEnd - utcNow) / (1000 * 60 * 60 * 24));
}

export function TenantDetailModal({
  tenantId,
  onClose,
}: {
  tenantId: number | null;
  onClose: () => void;
}) {
  const open = tenantId !== null;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="Détail tenant"
      size="xl"
    >
      {tenantId !== null ? (
        // Le `key={tenantId}` provoque un remount complet à chaque tenant
        // ouvert : ça reset state interne (data/error/loading) sans avoir
        // à le faire à la main dans un useEffect (interdit par
        // react-hooks/set-state-in-effect).
        <DetailContent key={tenantId} tenantId={tenantId} />
      ) : null}
    </Dialog>
  );
}

function DetailContent({ tenantId }: { tenantId: number }) {
  const [data, setData] = useState<TenantDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/superadmin/tenants/${tenantId}`)
      .then(async (res) => {
        const body = (await res.json().catch(() => null)) as
          | { success: true; data: TenantDetail }
          | { success: false; message?: string; error?: string }
          | null;
        if (cancelled) return;
        if (!res.ok || !body || body.success === false) {
          setError(
            (body && "message" in body && body.message) ||
              (body && "error" in body && body.error) ||
              `HTTP ${res.status}`,
          );
          return;
        }
        setData(body.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Erreur réseau");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (loading) {
    return <p className="text-sm text-muted-foreground">Chargement…</p>;
  }
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-danger-subtle px-3 py-2 text-sm text-danger">
        {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5">
      <header className="flex items-baseline justify-between gap-3 border-b border-border pb-3">
        <div>
          <h2 className="text-lg font-semibold">{data.name}</h2>
          <p className="font-mono text-xs text-muted-foreground">{data.slug}</p>
        </div>
        {data.admin_email && (
          <p className="text-xs text-muted-foreground">{data.admin_email}</p>
        )}
      </header>
      <SubscriptionSection detail={data} />
      <PaymentSection detail={data} />
      <UsageStatsSection detail={data} />
      <PastSubscriptionsSection detail={data} />
      <RecentActivitiesSection detail={data} />
    </div>
  );
}

function SubscriptionSection({ detail }: { detail: TenantDetail }) {
  const sub = detail.active_subscription;
  const trialEnd = sub?.trial_end ?? detail.trial_end_at ?? null;
  const daysLeft = daysBetween(trialEnd);
  const trialBadge =
    sub?.status === "trialing" && trialEnd ? (
      <Badge tone={daysLeft !== null && daysLeft <= 3 ? "danger" : "warning"}>
        {daysLeft !== null && daysLeft >= 0
          ? `${daysLeft} j restant${daysLeft > 1 ? "s" : ""}`
          : "Trial expiré"}
      </Badge>
    ) : null;

  return (
    <section>
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Abonnement
      </h3>
      <div className="rounded-lg border border-border bg-surface p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-lg font-semibold">
            {sub?.plan_name ?? "Aucun plan actif"}
          </span>
          {sub && (
            <Badge tone={SUB_STATUS_TONE[sub.status]}>
              {SUB_STATUS_LABEL[sub.status]}
            </Badge>
          )}
          {trialBadge}
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
          <Field label="Inscription">
            {dateFmt.format(new Date(detail.created_at))}
          </Field>
          {sub?.plan_monthly_price && (
            <Field label="Tarif mensuel">
              {priceFmt.format(Number(sub.plan_monthly_price))}
            </Field>
          )}
          {sub?.billing_period && (
            <Field label="Facturation">
              {sub.billing_period === "yearly" ? "Annuelle" : "Mensuelle"}
            </Field>
          )}
          {trialEnd && (
            <Field label="Fin de l'essai">
              {dateFmt.format(new Date(trialEnd))}
            </Field>
          )}
          {sub?.current_period_start && (
            <Field label="Période courante">
              {dateFmt.format(new Date(sub.current_period_start))}
              {sub.current_period_end &&
                ` → ${dateFmt.format(new Date(sub.current_period_end))}`}
            </Field>
          )}
          {sub?.canceled_at && (
            <Field label="Annulé le">
              {dateFmt.format(new Date(sub.canceled_at))}
            </Field>
          )}
          <Field label="Onboarding">
            {detail.onboarding_completed ? "Terminé" : "Non terminé"}
          </Field>
        </dl>
      </div>
    </section>
  );
}

function PaymentSection({ detail }: { detail: TenantDetail }) {
  const sub = detail.active_subscription;
  const customerId = sub?.stripe_customer_id ?? detail.stripe_customer_id;
  const subscriptionId =
    sub?.stripe_subscription_id ?? detail.stripe_subscription_id;

  const isPastDue = sub?.status === "past_due";
  const isCanceled = sub?.status === "canceled";

  return (
    <section>
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Paiement
      </h3>
      <div
        className={`rounded-lg border p-4 ${
          isPastDue
            ? "border-red-200 bg-danger-subtle"
            : "border-border bg-surface"
        }`}
      >
        {isPastDue && (
          <p className="mb-3 text-sm font-medium text-danger">
            ⚠ Le dernier prélèvement a échoué — facture en retard.
          </p>
        )}
        {isCanceled && (
          <p className="mb-3 text-sm text-muted-foreground">
            Abonnement annulé. Aucun prélèvement à venir.
          </p>
        )}
        {!isPastDue && !isCanceled && sub?.status === "active" && (
          <p className="mb-3 text-sm text-success">
            ✓ Prélèvement à jour. Prochaine échéance{" "}
            {sub.current_period_end
              ? dateFmt.format(new Date(sub.current_period_end))
              : "—"}
            .
          </p>
        )}
        {sub?.status === "trialing" && (
          <p className="mb-3 text-sm text-warning">
            Aucun prélèvement tant que la période d&apos;essai n&apos;est pas
            terminée.
          </p>
        )}

        <dl className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
          <Field label="Stripe customer">
            <code className="font-mono">{customerId ?? "—"}</code>
          </Field>
          <Field label="Stripe subscription">
            <code className="font-mono">{subscriptionId ?? "—"}</code>
          </Field>
        </dl>
        {customerId && (
          <p className="mt-3 text-[11px] text-muted-foreground">
            Pour voir l&apos;historique des paiements et factures Stripe,
            ouvrir le client dans le dashboard Stripe à partir du customer ID
            ci-dessus.
          </p>
        )}
      </div>
    </section>
  );
}

function UsageStatsSection({ detail }: { detail: TenantDetail }) {
  const items = [
    { label: "Techniciens", value: detail.stats.tech_count },
    { label: "Sites", value: detail.stats.site_count },
    { label: "Machines", value: detail.stats.machine_count },
    { label: "Interventions (total)", value: detail.stats.intervention_count },
    { label: "Interventions (30 j)", value: detail.stats.intervention_30j },
  ];
  return (
    <section>
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Usage
      </h3>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {items.map((it) => (
          <div
            key={it.label}
            className="rounded-lg border border-border bg-surface-2 px-3 py-2"
          >
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {it.label}
            </p>
            <p className="mt-0.5 text-lg font-semibold tabular-nums">
              {it.value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function PastSubscriptionsSection({ detail }: { detail: TenantDetail }) {
  if (detail.past_subscriptions.length === 0) return null;
  return (
    <section>
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Anciens abonnements
      </h3>
      <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
        {detail.past_subscriptions.map((s) => (
          <li
            key={s.id}
            className="flex items-center gap-3 px-3 py-2 text-sm"
          >
            <span className="font-medium">{s.plan_name ?? "—"}</span>
            <Badge tone="neutral">{s.status}</Badge>
            <span className="ml-auto text-xs text-muted-foreground tabular-nums">
              {s.canceled_at
                ? `annulé le ${dateFmt.format(new Date(s.canceled_at))}`
                : s.current_period_end
                  ? `fin ${dateFmt.format(new Date(s.current_period_end))}`
                  : "—"}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RecentActivitiesSection({ detail }: { detail: TenantDetail }) {
  if (detail.recent_activities.length === 0) return null;
  return (
    <section>
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Activité récente
      </h3>
      <ul className="divide-y divide-border rounded-lg border border-border bg-surface max-h-60 overflow-y-auto">
        {detail.recent_activities.map((a) => (
          <li key={a.id} className="px-3 py-2 text-xs">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[10px] text-muted-foreground">
                {a.type ?? "?"}
              </span>
              <span className="ml-auto text-muted-foreground tabular-nums">
                {dateTimeFmt.format(new Date(a.created_at))}
              </span>
            </div>
            {a.title && <div className="mt-0.5 font-medium">{a.title}</div>}
            {a.description && (
              <div className="mt-0.5 text-muted-foreground">
                {a.description}
              </div>
            )}
            {a.user_name?.trim() && (
              <div className="mt-0.5 italic text-[10px] text-muted-foreground">
                par {a.user_name}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-foreground">{children}</dd>
    </div>
  );
}
